import React from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  User, Calendar as CalendarIcon, Stethoscope, Edit2, 
  CheckCircle, XCircle, AlertCircle, UserPlus, RefreshCcw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../ui/dialog';
import { STATUS_CONFIG } from './constants';

const AppointmentDetail = ({
  isOpen,
  onClose,
  appointment,
  onEdit,
  onMarkCompleted,
  onCancel,
  onNoShow,
  onReschedule,
  onCreatePatientRecord
}) => {
  if (!appointment) return null;
  
  const statusConfig = STATUS_CONFIG[appointment.status] || STATUS_CONFIG.PROGRAMADA;
  const isPending = appointment.status === 'PROGRAMADA' || appointment.status === 'REPROGRAMADA';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="appointment-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-brand-main" />
            Detalle de Cita
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Estado:</span>
            <Badge className={`${statusConfig.color} border-0 text-sm px-3 py-1`}>
              {statusConfig.label}
            </Badge>
          </div>

          {/* Patient Info */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Paciente</p>
                <p className="font-semibold text-slate-900">{appointment.patient_name}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500">Edad</p>
                <p className="font-medium text-slate-700">{appointment.edad} años</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Teléfono</p>
                <p className="font-medium text-slate-700">{appointment.telefono}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500">Institución</p>
                <p className="font-medium text-slate-700">{appointment.institucion}</p>
              </div>
              {appointment.medico_referente && (
                <div>
                  <p className="text-xs text-slate-500">Médico Referente</p>
                  <p className="font-medium text-slate-700">{appointment.medico_referente}</p>
                </div>
              )}
            </div>
          </div>

          {/* Appointment Info */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-xs text-blue-600">Fecha y Hora</p>
                <p className="font-semibold text-blue-900">
                  {format(new Date(appointment.date + 'T12:00:00'), "EEEE, d 'de' MMMM yyyy", { locale: es })}
                </p>
                <p className="text-sm text-blue-700">
                  {appointment.hora_inicio} - {appointment.hora_fin} ({appointment.duracion} min)
                </p>
              </div>
            </div>
            
            <div>
              <p className="text-xs text-blue-600">Tipo de Examen</p>
              <p className="font-medium text-blue-900">{appointment.tipo_examen}</p>
            </div>

            {appointment.notes && (
              <div>
                <p className="text-xs text-blue-600">Notas</p>
                <p className="text-sm text-blue-800">{appointment.notes}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-2">
            {/* Primary Action - Create Patient Record */}
            {appointment.status !== 'CANCELADA' && appointment.status !== 'NO_ASISTIO' && (
              <Button 
                onClick={onCreatePatientRecord}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-12"
                data-testid="create-patient-record-button"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Crear Registro en Base de Datos
              </Button>
            )}

            {/* Status Action Buttons - Available for PROGRAMADA and REPROGRAMADA */}
            {isPending && (
              <>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Marcar como:</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    onClick={onMarkCompleted}
                    className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 h-11"
                    data-testid="mark-completed-button"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Se Presentó
                  </Button>
                  <Button 
                    onClick={onReschedule}
                    className="bg-purple-100 text-purple-700 hover:bg-purple-200 h-11"
                    data-testid="reschedule-button"
                  >
                    <RefreshCcw className="w-4 h-4 mr-2" />
                    Reprogramar
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    onClick={onCancel}
                    className="bg-red-100 text-red-600 hover:bg-red-200 h-11"
                    data-testid="cancel-appointment-button"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button 
                    onClick={onNoShow}
                    className="bg-amber-100 text-amber-600 hover:bg-amber-200 h-11"
                    data-testid="no-show-button"
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    No Asistió
                  </Button>
                </div>
              </>
            )}

            {/* Edit Button */}
            <Button 
              onClick={onEdit}
              variant="outline"
              className="w-full border-slate-300 text-slate-700"
              data-testid="edit-appointment-button"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Editar Cita
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentDetail;
